import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

const VS_SOURCE = `
  attribute vec4 aVertexPosition;
  void main() {
    gl_Position = aVertexPosition;
  }
`

const FS_SOURCE = `
  precision highp float;
  uniform vec2 iResolution;
  uniform float iTime;
  uniform vec3 uLineColor;
  uniform vec3 uBg1;
  uniform vec3 uBg2;

  const float overallSpeed = 0.2;
  const float gridSmoothWidth = 0.015;
  const float axisWidth = 0.05;
  const float majorLineWidth = 0.025;
  const float minorLineWidth = 0.0125;
  const float majorLineFrequency = 5.0;
  const float minorLineFrequency = 1.0;
  const float scale = 5.0;
  const float minLineWidth = 0.01;
  const float maxLineWidth = 0.2;
  const float lineSpeed = 1.0 * overallSpeed;
  const float lineAmplitude = 1.0;
  const float lineFrequency = 0.2;
  const float warpSpeed = 0.2 * overallSpeed;
  const float warpFrequency = 0.5;
  const float warpAmplitude = 1.0;
  const float offsetFrequency = 0.5;
  const float offsetSpeed = 1.33 * overallSpeed;
  const float minOffsetSpread = 0.6;
  const float maxOffsetSpread = 2.0;
  const int linesPerGroup = 16;

  #define drawCircle(pos, radius, coord) smoothstep(radius + gridSmoothWidth, radius, length(coord - (pos)))
  #define drawSmoothLine(pos, halfWidth, t) smoothstep(halfWidth, 0.0, abs(pos - (t)))
  #define drawCrispLine(pos, halfWidth, t) smoothstep(halfWidth + gridSmoothWidth, halfWidth, abs(pos - (t)))
  #define drawPeriodicLine(freq, width, t) drawCrispLine(freq / 2.0, width, abs(mod(t, freq) - (freq) / 2.0))

  float drawGridLines(float axis) {
    return drawCrispLine(0.0, axisWidth, axis)
          + drawPeriodicLine(majorLineFrequency, majorLineWidth, axis)
          + drawPeriodicLine(minorLineFrequency, minorLineWidth, axis);
  }

  float random(float t) {
    return (cos(t) + cos(t * 1.3 + 1.3) + cos(t * 1.4 + 1.4)) / 3.0;
  }

  float getPlasmaY(float x, float horizontalFade, float offset) {
    return random(x * lineFrequency + iTime * lineSpeed) * horizontalFade * lineAmplitude + offset;
  }

  void main() {
    vec2 fragCoord = gl_FragCoord.xy;
    vec2 uv = fragCoord.xy / iResolution.xy;
    vec2 space = (fragCoord - iResolution.xy / 2.0) / iResolution.x * 2.0 * scale;

    float horizontalFade = 1.0 - (cos(uv.x * 6.28) * 0.5 + 0.5);
    float verticalFade = 1.0 - (cos(uv.y * 6.28) * 0.5 + 0.5);

    space.y += random(space.x * warpFrequency + iTime * warpSpeed) * warpAmplitude * (0.5 + horizontalFade);
    space.x += random(space.y * warpFrequency + iTime * warpSpeed + 2.0) * warpAmplitude * horizontalFade;

    vec4 lines = vec4(0.0);
    vec4 lineColor = vec4(uLineColor, 1.0);
    vec4 bgColor1 = vec4(uBg1, 1.0);
    vec4 bgColor2 = vec4(uBg2, 1.0);

    for (int l = 0; l < linesPerGroup; l++) {
      float normalizedLineIndex = float(l) / float(linesPerGroup);
      float offsetTime = iTime * offsetSpeed;
      float offsetPosition = float(l) + space.x * offsetFrequency;
      float rand = random(offsetPosition + offsetTime) * 0.5 + 0.5;
      float halfWidth = mix(minLineWidth, maxLineWidth, rand * horizontalFade) / 2.0;
      float offset = random(offsetPosition + offsetTime * (1.0 + normalizedLineIndex)) * mix(minOffsetSpread, maxOffsetSpread, horizontalFade);
      float linePosition = getPlasmaY(space.x, horizontalFade, offset);
      float line = drawSmoothLine(linePosition, halfWidth, space.y) / 2.0 + drawCrispLine(linePosition, halfWidth * 0.15, space.y);

      float circleX = mod(float(l) + iTime * lineSpeed, 25.0) - 12.0;
      vec2 circlePosition = vec2(circleX, getPlasmaY(circleX, horizontalFade, offset));
      float circle = drawCircle(circlePosition, 0.01, space) * 4.0;

      line = line + circle;
      lines += line * lineColor * rand;
    }

    vec4 fragColor = mix(bgColor1, bgColor2, uv.x);
    fragColor *= verticalFade;
    fragColor.a = 1.0;
    fragColor += lines;

    gl_FragColor = fragColor;
  }
`

function loadShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type)
  if (!shader) return null
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }
  return shader
}

function initShaderProgram(
  gl: WebGLRenderingContext,
  vs: string,
  fs: string,
): WebGLProgram | null {
  const vertexShader = loadShader(gl, gl.VERTEX_SHADER, vs)
  const fragmentShader = loadShader(gl, gl.FRAGMENT_SHADER, fs)
  if (!vertexShader || !fragmentShader) return null

  const program = gl.createProgram()
  if (!program) return null
  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.linkProgram(program)
  gl.deleteShader(vertexShader)
  gl.deleteShader(fragmentShader)

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Shader program link error:', gl.getProgramInfoLog(program))
    gl.deleteProgram(program)
    return null
  }
  return program
}

export type ShaderBackgroundProps = {
  className?: string
  /** RGB bileşenleri 0–1 aralığında */
  lineColor?: [number, number, number]
  bgColor1?: [number, number, number]
  bgColor2?: [number, number, number]
  /** Zaman çarpanı (düşük = daha yavaş animasyon) */
  timeScale?: number
}

const DEFAULT_LINE: [number, number, number] = [0.4, 0.2, 0.8]
const DEFAULT_BG1: [number, number, number] = [0.1, 0.1, 0.3]
const DEFAULT_BG2: [number, number, number] = [0.3, 0.1, 0.5]

export function ShaderBackground({
  className,
  lineColor = DEFAULT_LINE,
  bgColor1 = DEFAULT_BG1,
  bgColor2 = DEFAULT_BG2,
  timeScale = 1,
}: ShaderBackgroundProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [cssFallback, setCssFallback] = useState(false)

  useEffect(() => {
    const root = rootRef.current
    const canvas = canvasRef.current
    if (!root || !canvas) return

    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
    })
    if (!gl) {
      console.warn('WebGL desteklenmiyor; shader arka planı devre dışı.')
      setCssFallback(true)
      return
    }

    const program = initShaderProgram(gl, VS_SOURCE, FS_SOURCE)
    if (!program) {
      setCssFallback(true)
      return
    }

    const positionBuffer = gl.createBuffer()
    if (!positionBuffer) {
      gl.deleteProgram(program)
      setCssFallback(true)
      return
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)

    const attrib = gl.getAttribLocation(program, 'aVertexPosition')
    const uResolution = gl.getUniformLocation(program, 'iResolution')
    const uTime = gl.getUniformLocation(program, 'iTime')
    const uLine = gl.getUniformLocation(program, 'uLineColor')
    const uBg1 = gl.getUniformLocation(program, 'uBg1')
    const uBg2 = gl.getUniformLocation(program, 'uBg2')

    if (
      attrib < 0 ||
      uResolution === null ||
      uTime === null ||
      uLine === null ||
      uBg1 === null ||
      uBg2 === null
    ) {
      gl.deleteBuffer(positionBuffer)
      gl.deleteProgram(program)
      setCssFallback(true)
      return
    }

    gl.useProgram(program)
    gl.uniform3f(uLine, lineColor[0], lineColor[1], lineColor[2])
    gl.uniform3f(uBg1, bgColor1[0], bgColor1[1], bgColor1[2])
    gl.uniform3f(uBg2, bgColor2[0], bgColor2[1], bgColor2[2])

    const setSize = () => {
      const w = root.clientWidth
      const h = root.clientHeight
      if (w === 0 || h === 0) return
      const dpr = Math.min(window.devicePixelRatio ?? 1, 2)
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      gl.viewport(0, 0, canvas.width, canvas.height)
    }

    const ro = new ResizeObserver(setSize)
    ro.observe(root)
    setSize()

    const start = performance.now()
    let raf = 0

    const render = (t: number) => {
      const currentTime = ((t - start) / 1000) * timeScale
      gl.clearColor(0, 0, 0, 1)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.useProgram(program)
      gl.uniform2f(uResolution, canvas.width, canvas.height)
      gl.uniform1f(uTime, currentTime)
      gl.uniform3f(uLine, lineColor[0], lineColor[1], lineColor[2])
      gl.uniform3f(uBg1, bgColor1[0], bgColor1[1], bgColor1[2])
      gl.uniform3f(uBg2, bgColor2[0], bgColor2[1], bgColor2[2])
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
      gl.vertexAttribPointer(attrib, 2, gl.FLOAT, false, 0, 0)
      gl.enableVertexAttribArray(attrib)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
      raf = requestAnimationFrame(render)
    }

    raf = requestAnimationFrame(render)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      gl.deleteBuffer(positionBuffer)
      gl.deleteProgram(program)
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
  }, [lineColor, bgColor1, bgColor2, timeScale])

  if (cssFallback) {
    const c1 = `rgb(${Math.round(bgColor1[0] * 255)}, ${Math.round(bgColor1[1] * 255)}, ${Math.round(bgColor1[2] * 255)})`
    const c2 = `rgb(${Math.round(bgColor2[0] * 255)}, ${Math.round(bgColor2[1] * 255)}, ${Math.round(bgColor2[2] * 255)})`
    return (
      <div
        className={cn('absolute inset-0', className)}
        style={{
          background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
        }}
        aria-hidden
      />
    )
  }

  return (
    <div ref={rootRef} className={cn('absolute inset-0', className)} aria-hidden>
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  )
}

export default ShaderBackground
